import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';

const DEVICE_ID_KEY = 'deviceId';

function getOrCreateDeviceId() {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = crypto.randomUUID ? crypto.randomUUID() : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = Math.random() * 16 | 0;
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [authStatus, setAuthStatus] = useState('loading');
  const [user, setUser] = useState(null);
  const deviceId = getOrCreateDeviceId();

  const fetchUser = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setAuthStatus('unauthenticated');
      setUser(null);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Device-Id': deviceId,
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        localStorage.setItem('is_superuser', userData.is_superuser);
        localStorage.setItem('subscriptionPlan', userData.subscription_plan);
        localStorage.setItem('allowedRegions', JSON.stringify(userData.regions || []));
        localStorage.setItem('hasVms', userData.has_vms ? '1' : '0');
        localStorage.setItem('hasBitacora', userData.has_bitacora ? '1' : '0');
        localStorage.setItem('hasModelAccess', userData.has_model_access ? '1' : '0');
        setAuthStatus('authenticated');
      } else {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('is_superuser');
        localStorage.removeItem('subscriptionPlan');
        localStorage.removeItem('allowedRegions');
        localStorage.removeItem('hasVms');
        localStorage.removeItem('hasBitacora');
        localStorage.removeItem('hasModelAccess');
        setUser(null);
        setAuthStatus('unauthenticated');
      }
    } catch (error) {
      console.error('Error validating token:', error);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('is_superuser');
      localStorage.removeItem('subscriptionPlan');
      localStorage.removeItem('allowedRegions');
      localStorage.removeItem('hasModelAccess');
      setUser(null);
      setAuthStatus('unauthenticated');
    }
  }, [deviceId]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (token) => {
    localStorage.setItem('accessToken', token);
    await fetchUser();
  };

  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('is_superuser');
    localStorage.removeItem('subscriptionPlan');
    localStorage.removeItem('allowedRegions');
    localStorage.removeItem('hasModelAccess');
    setUser(null);
    setAuthStatus('unauthenticated');
  };

  const isSuperuser = user ? user.is_superuser : false;
  const subscriptionPlan = user ? user.subscription_plan : null;
  const allowedRegions = user ? (user.regions || []) : [];
  const hasVms = user ? user.has_vms : false;
  const hasBitacora = user ? user.has_bitacora : false;
  const hasModelAccess = user ? user.has_model_access : false;

  const hasPlan = (requiredPlan) => {
    if (!subscriptionPlan) return false;
    if (isSuperuser) return true;

    const planHierarchy = {
      null: 0,
      'BOD Intelligence': 1,
      'BOD Forecast': 2,
      'Personalizado': 3
    };

    const userLevel = planHierarchy[subscriptionPlan] || 0;
    const requiredLevel = planHierarchy[requiredPlan] || 0;

    return userLevel >= requiredLevel;
  };

  const value = {
    authStatus,
    user,
    isSuperuser,
    subscriptionPlan,
    allowedRegions,
    hasVms,
    hasBitacora,
    hasModelAccess,
    deviceId,
    hasPlan,
    login,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};

export { getOrCreateDeviceId };
